import { Flex, IconTypesKeys } from "@evergis/ui";
import React, { FC, ReactNode, useCallback } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";

import { DraggableList } from "../../../DraggableList/DraggableList";
import { useDialogContext } from "../FullScreenDialog";
import {
  AttributeBlank,
  AttributesBlockContainer,
  AttributesList,
  RestrictedWidthContainer,
  SysAttributesListTitle,
} from "../styled";
import { ActionsDivider, AttributesActionsContainer, StickyShadowHide } from "./styled";
import { AddAttributeButton } from "./AddAttributeButton";
import { AddSystemAttributes } from "./AddSystemAttributes";
import { AttributesHeader } from "./AttributesHeader";
import { AttributesLoader } from "./AttributesLoader";
import { reoderList } from "../../../../utils/array";
import { Attribute, useAttributesToRender } from "./hooks/useAttributesToRender";

interface HeaderConfiguration {
  icon?: IconTypesKeys;
  label?: string;
  description?: string;
}

export interface AttributesProps<A = Attribute> {
  attributes: A[];
  setAttributes?: (newAttrs: A[]) => void;
  children: (attribute: A) => ReactNode;
  draggable?: boolean;
  onAddAttribute?: VoidFunction;
  addAttributeDisabled?: boolean;
  loading?: boolean;
  showHeader?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  className?: string;
  isEditType?: boolean;
  sysAttributes?: A[];
  excludeSysAttributes?: string[];
  addSysAttributes?: (newSysAttrs: A[]) => void;
  allAttributes?: boolean;
  allAttributesLabel?: string;
  toggleAllAttributes?: VoidFunction;
  isEdit?: boolean;
  headerConfiguration?: HeaderConfiguration;
  actions?: ReactNode;
}

// eslint-disable-next-line max-lines-per-function
export const Attributes: FC<AttributesProps> = ({
  attributes,
  setAttributes,
  onAddAttribute,
  addAttributeDisabled,
  draggable = true,
  children,
  loading,
  showHeader = true,
  showTitle = true,
  showDescription = true,
  className,
  isEditType,
  sysAttributes,
  excludeSysAttributes,
  addSysAttributes,
  allAttributes,
  allAttributesLabel,
  toggleAllAttributes,
  isEdit,
  headerConfiguration,
  actions,
}) => {
  const { t } = useTranslation("dialogs");
  const { attributes: attributesContext } = useDialogContext();
  const attributesToRender = useAttributesToRender(attributes);

  const onDragEnd = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination) return;
      const newAttrs = reoderList(attributes, source.index, destination.index);

      setAttributes(newAttrs);
    },
    [attributes, setAttributes],
  );

  const hasAdd = !!onAddAttribute && isEditType;
  const hasActionsBar = hasAdd || !!addSysAttributes || !!actions;
  const onlyAddSysAttributes = !!addSysAttributes && !hasAdd;

  return (
    <AttributesBlockContainer noPaddingBottom={hasActionsBar} className={className}>
      {showHeader && (
        <AttributesHeader
          allAttributes={allAttributes}
          allAttributesLabel={allAttributesLabel}
          showTitle={showTitle}
          showDescription={showDescription}
          toggleAllAttributes={toggleAllAttributes}
          isEdit={isEdit}
          headerConfiguration={headerConfiguration}
        />
      )}
      <RestrictedWidthContainer>
        <AttributesList>
          {draggable ? (
            <DraggableList
              onDragEnd={onDragEnd}
              id={"attribute-list"}
              items={attributesToRender as Record<string, unknown>[]}
              getId={(item, index) => `${index + 1}`}
            >
              {(attribute, provided) => (
                <AttributeBlank {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                  {children(attribute as Attribute)}
                </AttributeBlank>
              )}
            </DraggableList>
          ) : (
            <>
              {attributesToRender.map(attribute => (
                <AttributeBlank key={attribute.id}>{children(attribute)}</AttributeBlank>
              ))}
            </>
          )}
        </AttributesList>
        {sysAttributes && sysAttributes.length > 0 && (
          <>
            <SysAttributesListTitle>{t("dialogComponents.systemAttributes")}</SysAttributesListTitle>
            {sysAttributes.map(attribute => (
              <AttributeBlank key={attribute.id}>{children(attribute)}</AttributeBlank>
            ))}
          </>
        )}
        {loading && <AttributesLoader />}
      </RestrictedWidthContainer>
      {hasActionsBar && (
        <>
          <StickyShadowHide />
          <AttributesActionsContainer>
            <RestrictedWidthContainer>
              <Flex style={{ alignItems: "center" }}>
                {actions}
                {!onlyAddSysAttributes && hasAdd && (
                  <AddAttributeButton disabled={addAttributeDisabled} onClick={onAddAttribute}>
                    {attributesContext.addAttribute}
                  </AddAttributeButton>
                )}
                {addSysAttributes && hasAdd && !onlyAddSysAttributes && (
                  <>
                    <ActionsDivider vertical />
                    <AddSystemAttributes
                      excludeSysAttributes={excludeSysAttributes}
                      sysAttributes={sysAttributes}
                      addSysAttributes={addSysAttributes}
                    />
                  </>
                )}
                {onlyAddSysAttributes && (
                  <AddSystemAttributes
                    excludeSysAttributes={excludeSysAttributes}
                    withButton
                    sysAttributes={sysAttributes}
                    addSysAttributes={addSysAttributes}
                  />
                )}
              </Flex>
            </RestrictedWidthContainer>
          </AttributesActionsContainer>
        </>
      )}
    </AttributesBlockContainer>
  );
};
